import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { useCreateCustomer } from './hooks/useCustomerMutations';
import { useAuthStore } from '../../store/authStore';
import type { AccountStatus, CreateCustomerInput, CustomerType } from '../../types/api';
import styles from './CustomerCreatePage.module.css';

const CUSTOMER_TYPE_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
];

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

interface FormState {
  customer_type: CustomerType;
  name: string;
  phone: string;
  email: string;
  phone2: string;
  address: string;
  postcode: string;
  company_name: string;
  vat_number: string;
  payment_terms: string;
  credit_limit: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  account_status: AccountStatus;
}

const INITIAL_STATE: FormState = {
  customer_type: 'residential',
  name: '',
  phone: '',
  email: '',
  phone2: '',
  address: '',
  postcode: '',
  company_name: '',
  vat_number: '',
  payment_terms: '',
  credit_limit: '',
  account_name: '',
  account_number: '',
  sort_code: '',
  account_status: 'active',
};

export function CustomerCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [duplicateWarning, setDuplicateWarning] = useState<{
    message: string;
    existing_uuid: string;
  } | null>(null);

  const createMutation = useCreateCustomer();

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const validationErrors = useMemo(() => {
    const issues: string[] = [];
    if (!form.name.trim()) issues.push('Name is required.');
    if (!form.address.trim()) issues.push('Address is required.');
    if (!form.postcode.trim()) issues.push('Postcode is required.');
    return issues;
  }, [form]);

  const canSubmit = validationErrors.length === 0 && !createMutation.isPending;

  async function handleSubmit(force = false) {
    if (!canSubmit) return;

    const input: CreateCustomerInput = {
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || null,
      phone2: form.phone2.trim() || null,
      address: form.address.trim(),
      postcode: form.postcode.trim().toUpperCase(),
      customer_type: form.customer_type,
      account_status: form.account_status,
    };

    if (form.customer_type === 'commercial') {
      input.company_name = form.company_name.trim() || null;
      input.vat_number = form.vat_number.trim() || null;
      input.payment_terms = form.payment_terms.trim() || null;
      input.credit_limit = form.credit_limit ? Number(form.credit_limit) : null;
    }

    if (isAdmin) {
      input.account_name = form.account_name.trim() || null;
      input.account_number = form.account_number.trim() || null;
      input.sort_code = form.sort_code.trim() || null;
    }

    if (force) {
      (input as unknown as Record<string, unknown>).force = true;
    }

    try {
      const res = await createMutation.mutateAsync(input);
      const customer = res.data;
      toast.success('Customer created');
      navigate(`/customers/${encodeURIComponent(customer.uuid)}`);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { data?: { type?: string; message?: string; existing_uuid?: string } } } };
      if (axiosErr.response?.status === 409) {
        const data = axiosErr.response.data?.data;
        if (data?.type === 'potential_duplicate') {
          setDuplicateWarning({
            message: data.message ?? 'A potential duplicate customer was found.',
            existing_uuid: data.existing_uuid ?? '',
          });
          return;
        }
      }
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Customer"
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/customers')}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
        }
      />

      <Card title="Customer details">
        <div className={styles.grid2}>
          <Select
            label="Customer type"
            options={CUSTOMER_TYPE_OPTIONS}
            value={form.customer_type}
            onChange={(e) => setField('customer_type', e.target.value as CustomerType)}
          />
          <div />
          <Input
            label="Name"
            required
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />
          <Input
            label="Phone"
            variant="tel"
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
          />
          <Input
            label="Email"
            variant="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
          />
          <Input
            label="Phone (alt)"
            variant="tel"
            value={form.phone2}
            onChange={(e) => setField('phone2', e.target.value)}
          />
          <Input
            label="Address"
            required
            value={form.address}
            onChange={(e) => setField('address', e.target.value)}
          />
          <Input
            label="Postcode"
            required
            value={form.postcode}
            onChange={(e) => setField('postcode', e.target.value.toUpperCase())}
          />
        </div>
      </Card>

      {form.customer_type === 'commercial' ? (
        <Card title="Business details">
          <div className={styles.grid2}>
            <Input
              label="Company name"
              value={form.company_name}
              onChange={(e) => setField('company_name', e.target.value)}
            />
            <Input
              label="VAT number"
              value={form.vat_number}
              onChange={(e) => setField('vat_number', e.target.value)}
            />
            <Input
              label="Payment terms"
              value={form.payment_terms}
              onChange={(e) => setField('payment_terms', e.target.value)}
            />
            <Input
              label="Credit limit (£)"
              variant="number"
              value={form.credit_limit}
              onChange={(e) => setField('credit_limit', e.target.value)}
            />
          </div>
        </Card>
      ) : null}

      {isAdmin ? (
        <Card title="Banking details">
          <div className={styles.grid2}>
            <Input
              label="Account name"
              value={form.account_name}
              onChange={(e) => setField('account_name', e.target.value)}
            />
            <Input
              label="Account number"
              value={form.account_number}
              onChange={(e) => setField('account_number', e.target.value)}
            />
            <Input
              label="Sort code (NN-NN-NN)"
              value={form.sort_code}
              onChange={(e) => setField('sort_code', e.target.value)}
            />
            <Select
              label="Account status"
              options={ACCOUNT_STATUS_OPTIONS}
              value={form.account_status}
              onChange={(e) => setField('account_status', e.target.value as AccountStatus)}
            />
          </div>
        </Card>
      ) : null}

      <div className={styles.submitBar}>
        <ul className={styles.errorList} aria-live="polite">
          {validationErrors.slice(0, 4).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
        <div className={styles.submitActions}>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            loading={createMutation.isPending}
            disabled={!canSubmit}
          >
            Create Customer
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={duplicateWarning !== null}
        title="Potential duplicate found"
        message={duplicateWarning?.message ?? ''}
        confirmLabel="Create anyway"
        cancelLabel="View existing"
        variant="default"
        onCancel={() => {
          if (duplicateWarning?.existing_uuid) {
            navigate(`/customers/${encodeURIComponent(duplicateWarning.existing_uuid)}`);
          }
          setDuplicateWarning(null);
        }}
        onConfirm={() => {
          setDuplicateWarning(null);
          void handleSubmit(true);
        }}
      />
    </div>
  );
}
